// src/components/Learning.js
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Avatar,
  Fade,
} from '@mui/material';
import {
  School,
  ArrowBack,
  Logout,
  Person,
  AccessTime,
  OndemandVideo,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ref, get, child } from 'firebase/database';
import { database, auth } from '../firebase/Config';

const Learning = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const dbRef = ref(database);
      const coursesSnapshot = await get(child(dbRef, 'courses'));
      
      if (coursesSnapshot.exists()) {
        const coursesData = coursesSnapshot.val();
        
        // Buscar número de aulas de cada curso
        const lessonsSnapshot = await get(child(dbRef, 'lessons'));
        const lessonsData = lessonsSnapshot.exists() ? lessonsSnapshot.val() : {};
        
        const coursesArray = Object.keys(coursesData).map((key) => {
          // Contar aulas deste curso
          const lessonsCount = Object.values(lessonsData).filter(
            lesson => lesson.courseId === key
          ).length;
          
          return {
            id: key,
            ...coursesData[key],
            lessonsCount,
          };
        });
        
        setCourses(coursesArray);
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error('Erro ao buscar cursos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleCourseClick = (courseId) => {
    navigate(`/learning/course/${courseId}`);
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            onClick={() => navigate('/home')}
            sx={{
              color: '#fff',
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <School sx={{ mr: 2, fontSize: 32 }} />
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Aprendizado & Suporte
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#48bb78' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Box>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#2d3748',
                  mb: 2,
                }}
              >
                Cursos Disponíveis
              </Typography>
              <Typography variant="body1" sx={{ color: '#718096', mb: 1 }}>
                Aprenda a utilizar todas as funcionalidades do AAP-GIS através dos nossos cursos em vídeo
              </Typography>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Total de cursos: <strong>{courses.length}</strong>
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress size={60} sx={{ color: '#48bb78' }} />
              </Box>
            ) : courses.length === 0 ? (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: 6,
                  textAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <School sx={{ fontSize: 80, color: '#cbd5e0', mb: 2 }} />
                <Typography variant="h5" sx={{ color: '#718096', mb: 1 }}>
                  Nenhum curso disponível no momento
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                  Novos cursos serão adicionados em breve!
                </Typography>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {courses.map((course) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
                    <Fade in timeout={800}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          overflow: 'hidden',
                          transition: 'all 0.3s',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                          },
                        }}
                      >
                        <CardActionArea onClick={() => handleCourseClick(course.id)} sx={{ height: '100%' }}>
                          {course.thumbnail ? (
                            <CardMedia
                              component="img"
                              height="180"
                              image={course.thumbnail}
                              alt={course.title}
                              sx={{ objectFit: 'cover' }}
                            />
                          ) : (
                            <Box
                            sx={{
                              height: 180,
                              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                              <School sx={{ fontSize: 80, color: 'rgba(255, 255, 255, 0.5)' }} />
                            </Box>
                          )}
                          
                          <CardContent sx={{ flexGrow: 1, p: 3 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: '#2d3748',
                                mb: 2,
                                minHeight: 60,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {course.title}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: '#718096',
                                mb: 2,
                                minHeight: 60,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {course.description}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Person sx={{ fontSize: 18, color: '#718096' }} />
                              <Typography variant="body2" sx={{ color: '#718096' }}>
                                {course.instructor}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              {course.duration && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AccessTime sx={{ fontSize: 18, color: '#718096' }} />
                                  <Typography variant="body2" sx={{ color: '#718096' }}>
                                    {course.duration}
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <OndemandVideo sx={{ fontSize: 18, color: '#718096' }} />
                                <Typography variant="body2" sx={{ color: '#718096' }}>
                                  {course.lessonsCount} {course.lessonsCount === 1 ? 'aula' : 'aulas'}
                                </Typography>
                              </Box>
                            </Box>

                            <Chip
                              label="Acessar Curso"
                              size="small"
                              sx={{
                                bgcolor: '#48bb78',
                                color: '#fff',
                                fontWeight: 600,
                                '&:hover': {
                                  bgcolor: '#38a169',
                                },
                              }}
                            />
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Learning;
